import type { ParsedToken } from "@/lib/search";

import type { LogEntry } from "./useReadingLog";

/**
 * Whether a token is a word you can pivot on.
 *
 * Punctuation and whitespace get no underline and no hover target, so they must not be Tab stops
 * either — otherwise reading a sentence by keyboard stops on every 、and 。
 */
export function isWord(token: ParsedToken): boolean {
  return (
    token.hasEntries ||
    /[\p{Script=Han}\p{Script=Hiragana}\p{Script=Katakana}]/u.test(token.surface)
  );
}

/** A single kanji. `Script=Han` is the same test `isWord` uses, narrowed to one code point. */
const KANJI = /^\p{Script=Han}$/u;

/**
 * Which character offsets in a sentence can be pivoted on, and which of them are inside a word.
 *
 * **This is 日本-512's whole rule, and it is the A1/A2 switch expressed as one function.** Before
 * it, the pivot was always `token.start` — so a word beginning at an interior offset of a token
 * had no route at all, and hovering every token of
 * `彼は大学生活を…この豪華な弓道場がある。` reached 21 terms with **学生 and 道場 among none of
 * them**. `POST /api/search/lookup` had always answered those (index 3 of that sentence returns
 * 学生 · 学生 · 学); the client simply could not ask.
 *
 * **A1: a token start is always a target, and so is any KANJI inside a multi-character token.**
 * Kana interiors are deliberately left out, and that is measured rather than tasteful. Rows
 * returned per pivot class over 300 of Alex's own sentences:
 *
 * | pivot            |   n  | median | p90 | max |
 * |------------------|-----:|-------:|----:|----:|
 * | interior-kanji   |  429 |    3   |   9 |  21 |
 * | start-kanji      |  881 |    4   |  10 |  27 |
 * | start-kana       | 2508 |    9   |  33 |  90 |
 * | interior-kana    | 2237 |   14   |  52 | 123 |
 *
 * So an interior kanji is the **quietest** thing on the screen — quieter than the token starts the
 * dock already shows all day — while an interior kana is by far the noisiest (pivoting the し of
 * 楽しんでいる returns 75 rows of 新 / 真 / 信 / 辛). Yomitan pivots on every character and lives
 * with that because its reader *aims a cursor*; ours is driven by a hover sweep, which is not the
 * same act. If the kana half is ever wanted it belongs behind a held modifier — A2 on the ticket's
 * canvas — and it is a change to this function and to nothing else.
 *
 * Positions inside punctuation and whitespace are not targets at all: `isWord` already decides
 * that for the token, and a `。` must not be a hover stop.
 */
export interface PivotTarget {
  /** The character offset into the sentence. This is what `POST /api/search/lookup` takes. */
  index: number;
  /** The token this offset falls in — what the FORM strip describes and what ⇥ walks. */
  tokenIndex: number;
  /** False at a token start, true for a kanji reached inside one. */
  interior: boolean;
}

export function pivotTargets(tokens: readonly ParsedToken[]): PivotTarget[] {
  const targets: PivotTarget[] = [];
  tokens.forEach((token, tokenIndex) => {
    if (!isWord(token)) return;
    // Spread once per token rather than once per character: `[...surface]` is O(n) and doing it
    // inside the loop made a long token quadratic, on a function every sentence in the log runs.
    const chars = [...token.surface];
    // `length` is authoritative rather than `surface.length`: both are code-point counts the
    // server produced together, and `start` is an offset into the same string, so deriving one
    // from the other here is one place for them to disagree.
    for (let i = 0; i < token.length; i += 1) {
      const interior = i > 0;
      // A single-character token has no interior, so the guard below never fires for it — but
      // spelling the whole condition out keeps the rule readable as one sentence.
      if (interior && !KANJI.test(chars[i] ?? "")) continue;
      targets.push({ index: token.start + i, tokenIndex, interior });
    }
  });
  return targets;
}

/**
 * The token an absolute character offset falls in, or null.
 *
 * The FORM strip, the mining source and the added-card panel all still speak in tokens — a pivot
 * says *where you are looking*, and the token is still *what word you are in*. Hovering the 道 of
 * 弓道 answers 道場 in the dock while the strip goes on describing 弓道, which is correct: the
 * strip explains why the text on screen does not match the dictionary, and the text on screen is
 * still 弓道.
 */
export function tokenAt(
  tokens: readonly ParsedToken[],
  index: number,
): { token: ParsedToken; tokenIndex: number } | null {
  for (let i = 0; i < tokens.length; i += 1) {
    const token = tokens[i]!;
    if (index >= token.start && index < token.start + token.length) {
      return { token, tokenIndex: i };
    }
  }
  return null;
}

export interface Selection {
  /** A sentence id — a UUID string since 日本-207 made the log survive a reload. */
  entryId: string;
  /**
   * The CHARACTER offset being pivoted on — not a token index (日本-512).
   *
   * It was a token index for as long as a token start was the only thing reachable. Everything
   * that needs the token asks `tokenAt`, which is one lookup rather than a second field that
   * could disagree with this one.
   */
  index: number;
}

/**
 * The next (or previous) word within the sentence being read.
 *
 * Stays inside one sentence and wraps at its end rather than walking into the next capture: Tab is
 * "read this line through", and silently jumping to another sentence would move the dock's answer
 * out from under a question you were still looking at.
 *
 * **The sentence walked is the FOCUSED one** (日本-729), falling back to the selection's and then to
 * the newest capture. It was the selection's alone, and `↑`/`↓` move the focus without moving the
 * selection — so after arrowing to an older row, ⇥ went on walking the row you had left. Arriving
 * from another sentence starts at the edge you are moving away from: the first word going forward,
 * the last going back.
 *
 * **⇥ walks WORDS, not pivot targets** (日本-512). Sub-token targets are a pointer affordance; a
 * Tab that stopped three times inside 弓道場 would stop being "read this line through" and become
 * a way to page through characters. So this walks token starts exactly as it always did — what
 * changed is only that the offsets it returns are absolute rather than token indices.
 */
export function nextWord(
  entries: readonly LogEntry[],
  current: Selection | null,
  direction: 1 | -1,
  focusedId: string | null = null,
): Selection | null {
  const byId = (id: string | null | undefined) =>
    id == null ? undefined : entries.find((e) => e.id === id);
  const entry = byId(focusedId) ?? byId(current?.entryId) ?? entries[entries.length - 1];
  if (!entry) return current;

  const words = entry.tokens.filter(isWord).map((token) => token.start);
  if (words.length === 0) return current;

  if (!current || current.entryId !== entry.id) {
    return { entryId: entry.id, index: direction === 1 ? words[0]! : words[words.length - 1]! };
  }

  // The current offset may be INSIDE a word rather than at its start — that is the whole of
  // 日本-512 — so ⇥ from the 道 of 弓道場 must reach the word after 弓道, not refuse to move and
  // not restart from the edge. Resolve to the containing word first, and only fall back to the
  // edge when the offset belongs to no word at all (punctuation, or a re-parse shifted things).
  const containing = tokenAt(entry.tokens, current.index);
  const position =
    containing && isWord(containing.token) ? words.indexOf(containing.token.start) : -1;
  const from = position === -1 ? (direction === 1 ? -1 : words.length) : position;
  const target = words[(from + direction + words.length) % words.length]!;
  return { entryId: entry.id, index: target };
}
